import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  fetchGuardianRules,
  deletePersonalizedRule,
  updateRule,
} from "../../../../../api/finances/guardianAPI";
import Tooltip from "../../../../../components/ui/Tooltip";
import ActionMenu from "../../../../../components/ui/ActionMenu";
import ConfirmationModal from "../../../../../components/ui/popup/ConfirmationModal";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import GhostSpendSafeguards from "../../../../../components/ui/loading/skeletons/GhostSpendSafeguards";
import { getSafeguardRuleLimit } from "../../../../../utils/configs/finances/planLimits";

function SpendSafeguards({
  content,
  isPaidPlan,
  refreshTrigger,
  onUpgradeClick,
  onAddRuleClick,
  onEditRuleClick,
}) {
  const guardian = content.subscriber_view.money_guardian;
  const copy = guardian.spend_safeguards;

  const planTier = useSelector(
    (state) => state.auth.user?.financial_insights?.plan_code || "fi_free",
  );

  const [defaultRules, setDefaultRules] = useState([]);
  const [personalizedRules, setPersonalizedRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // --- Confirmation Modal State Infrastructure ---
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [confirmModalError, setConfirmModalError] = useState("");

  const [pendingAction, setPendingAction] = useState({
    mode: null,
    rule: null,
    isDefault: false,
  });

  // Calculate dynamic limit state: sum active default rules + custom rules
  const activeDefaultCount = defaultRules.filter(
    (rule) => rule.isEnable,
  ).length;
  const totalActiveRules = activeDefaultCount + personalizedRules.length;

  const maxRulesAllowed = getSafeguardRuleLimit(planTier, "spend_safeguards");
  const isLimitReached = totalActiveRules >= maxRulesAllowed;

  const loadRules = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setErrorMessage(null);
      const response = await fetchGuardianRules();

      if (response.success) {
        const { defaultRules, personalizedRules } = response.data;

        const stableDefaultRules = [...defaultRules].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        const stablePersonalizedRules = [...personalizedRules].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setDefaultRules(stableDefaultRules);
        setPersonalizedRules(stablePersonalizedRules);
      } else {
        setErrorMessage(response.error || guardian.errors.load_fail);
      }
    } catch (err) {
      setErrorMessage(guardian.errors.unexpected);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, [refreshTrigger]);

  const handleToggleClick = (
    ruleUuid,
    ruleName,
    currentIsEnable,
    isDefault,
  ) => {
    setConfirmModalError("");
    setPendingAction({
      mode: "toggle",
      rule: { uuid: ruleUuid, name: ruleName, isEnable: currentIsEnable },
      isDefault,
    });
    setIsConfirmModalOpen(true);
  };

  const handleDeleteClick = (ruleUuid, ruleName) => {
    setConfirmModalError("");
    setPendingAction({
      mode: "delete",
      rule: { uuid: ruleUuid, name: ruleName },
      isDefault: false,
    });
    setIsConfirmModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction.mode || !pendingAction.rule) return;

    const { mode, rule, isDefault } = pendingAction;
    setConfirmModalError("");
    setIsProcessingAction(true);

    if (mode === "toggle") {
      const updatedStatus = !rule.isEnable;
      const result = await updateRule(rule.uuid, {
        type: isDefault ? "default" : "custom",
        isEnable: updatedStatus,
      });

      if (result.success) {
        if (isDefault) {
          setDefaultRules((prev) =>
            prev.map((r) =>
              r.uuid === rule.uuid ? { ...r, isEnable: updatedStatus } : r,
            ),
          );
        } else {
          setPersonalizedRules((prev) =>
            prev.map((r) =>
              r.uuid === rule.uuid ? { ...r, isEnable: updatedStatus } : r,
            ),
          );
        }

        setIsConfirmModalOpen(false);
        setPendingAction({ mode: null, rule: null, isDefault: false });
        loadRules(true);
      } else {
        setConfirmModalError(result.error || guardian.errors.toggle_fail);
      }
    } else if (mode === "delete") {
      const result = await deletePersonalizedRule(rule.uuid);

      if (result.success) {
        setIsConfirmModalOpen(false);
        setPendingAction({ mode: null, rule: null, isDefault: false });
        loadRules();
      } else {
        setConfirmModalError(result.error || guardian.errors.delete_fail);
      }
    }

    setIsProcessingAction(false);
  };

  if (errorMessage) {
    return (
      <div className="error-text-container" style={{ padding: "16px" }}>
        {errorMessage}
      </div>
    );
  }

  const targetRuleName = pendingAction.rule?.name;
  let modalMessage = "";
  let confirmCtaText = isProcessingAction
    ? guardian.modals.processing_cta
    : guardian.actions.update;

  if (pendingAction.mode === "toggle") {
    const incomingActionState = pendingAction.rule?.isEnable
      ? guardian.labels.disabled
      : guardian.labels.enabled;

    modalMessage = guardian.modals.toggle_message
      .replace("{action}", incomingActionState.toLowerCase())
      .replace("{name}", targetRuleName);

    confirmCtaText = isProcessingAction
      ? guardian.modals.processing_cta
      : incomingActionState;
  } else if (pendingAction.mode === "delete") {
    modalMessage = guardian.modals.delete_message.replace(
      "{name}",
      targetRuleName,
    );
    confirmCtaText = isProcessingAction
      ? guardian.modals.deleting_cta
      : guardian.actions.delete;
  }

  const hasRules = defaultRules.length > 0 || personalizedRules.length > 0;

  const renderDropdownToggle = (rule, isDefault) => {
    const uniqueSwitchId = `dropdown-switch-${
      isDefault ? "default" : "custom"
    }-${rule.uuid}`;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "16px",
        }}
      >
        <span>
          {rule.isEnable ? guardian.labels.enabled : guardian.labels.disabled}
        </span>
        <div className="switch-container" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            id={uniqueSwitchId}
            className="edit-switch"
            checked={rule.isEnable}
            onChange={() =>
              handleToggleClick(rule.uuid, rule.name, rule.isEnable, isDefault)
            }
          />
          <label htmlFor={uniqueSwitchId} className="switch-label">
            <span className="switch-slider"></span>
          </label>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="standard-container">
        <div className="guardian-section-header">
          <Tooltip content={copy.desc_tooltip}>
            <h4 className="h4-tooltip">{copy.title}</h4>
          </Tooltip>
          <div style={{ marginTop: "16px" }}>
            {!isPaidPlan ? (
              <p className="reset-margin free-plan-msg">
                <Tooltip content={copy.unsubscribe_add_rule_marketing}>
                  <span
                    style={{
                      color: "#B45309",
                      cursor: "pointer",
                      textDecoration: "underline dashed",
                    }}
                  >
                    {copy.unsubscribe_add_rule_msg}
                  </span>
                </Tooltip>
                <span
                  onClick={onUpgradeClick}
                  style={{
                    marginLeft: "10px",
                    color: "#3b82f6",
                    cursor: "pointer",
                  }}
                >
                  {copy.unsubscribe_add_rule_cta}
                </span>
              </p>
            ) : isLimitReached ? (
              <span style={{ fontSize: "12px", color: "#6B7280" }}>
                Maximum limit reached ({totalActiveRules}/{maxRulesAllowed}{" "}
                rules)
              </span>
            ) : (
              <button
                className="cta-button primary small"
                onClick={onAddRuleClick}
                style={{
                  padding: "8px 14px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                {copy.add_rule_cta}
              </button>
            )}
          </div>
        </div>

        <div className="rules-list-group">
          <LoadingToRealTransitionWrapper
            isLoading={isLoading && !hasRules}
            ghost={<GhostSpendSafeguards count={3} />}
            disableDefaultStyles={true}
            style={{ gap: "0px" }}
          >
            {/* System/Default Rules */}
            {defaultRules.map((rule, index) => {
              const rowKey = `row-default-${rule.uuid}`;

              const defaultRuleActions = [
                {
                  label: renderDropdownToggle(rule, true),
                  onClick: () =>
                    handleToggleClick(
                      rule.uuid,
                      rule.name,
                      rule.isEnable,
                      true,
                    ),
                },
              ];

              return (
                <div
                  className="rule-item"
                  key={rowKey}
                  style={{
                    borderBottom:
                      index === defaultRules.length - 1 &&
                      personalizedRules.length === 0
                        ? "none"
                        : "1px solid #E5E7EB",
                  }}
                >
                  <div className="rule-item-left">
                    <div className="rule-item-left-name-container">
                      <span className="rule-item-name-text">{rule.name}</span>
                      <span className="rule-item-text-badget-system">
                        {guardian.labels.system}
                      </span>
                      {!rule.isEnable && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 500,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            marginLeft: "6px",
                          }}
                        >
                          {guardian.labels.disabled}
                        </span>
                      )}
                    </div>
                    <span className="rule-item-description">
                      {rule.description}
                    </span>
                  </div>

                  <div
                    className="rule-item-right"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Tooltip content={copy.threshold_tooltip}>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: rule.isEnable ? "#1F2937" : "#9CA3AF",
                          borderBottom: `1px dashed ${
                            rule.isEnable ? "#1F2937" : "#9CA3AF"
                          }`,
                        }}
                      >
                        $
                        {rule.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </Tooltip>
                    <div className="item-actions">
                      <ActionMenu actions={defaultRuleActions} />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Custom Rules */}
            {personalizedRules.map((rule, index) => {
              const rowKey = `row-custom-${index}-${rule.uuid}`;

              const customRuleActions = [
                {
                  label: renderDropdownToggle(rule, false),
                  onClick: () =>
                    handleToggleClick(
                      rule.uuid,
                      rule.name,
                      rule.isEnable,
                      false,
                    ),
                },
                {
                  label: guardian.actions.update,
                  onClick: () => onEditRuleClick(rule),
                },
                {
                  label: guardian.actions.delete,
                  className: "delete-action-item",
                  onClick: () => handleDeleteClick(rule.uuid, rule.name),
                },
              ];

              return (
                <div
                  className="rule-item"
                  key={rowKey}
                  style={{
                    borderBottom:
                      index === personalizedRules.length - 1
                        ? "none"
                        : "1px solid #E5E7EB",
                  }}
                >
                  <div className="rule-item-left">
                    <div className="rule-item-left-name-container">
                      <span className="rule-item-name-text">{rule.name}</span>
                      <span className="rule-item-text-badget-custom">
                        {guardian.labels.custom}
                      </span>
                      {!rule.isEnable && (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 500,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                            marginLeft: "6px",
                          }}
                        >
                          {guardian.labels.disabled}
                        </span>
                      )}
                    </div>
                    <span className="rule-item-description">
                      {rule.description}
                    </span>
                  </div>

                  <div
                    className="rule-item-right"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Tooltip content={copy.threshold_tooltip}>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: rule.isEnable ? "#1F2937" : "#9CA3AF",
                          borderBottom: `1px dashed ${
                            rule.isEnable ? "#1F2937" : "#9CA3AF"
                          }`,
                        }}
                      >
                        $
                        {rule.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </Tooltip>
                    <div className="item-actions">
                      <ActionMenu actions={customRuleActions} />
                    </div>
                  </div>
                </div>
              );
            })}

            {!isLoading && !hasRules && (
              <div
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                {guardian.labels.no_rules}
              </div>
            )}
          </LoadingToRealTransitionWrapper>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setPendingAction({ mode: null, rule: null, isDefault: false });
        }}
        onConfirm={handleConfirmAction}
        message={modalMessage}
        confirmText={confirmCtaText}
        cancelText={guardian.modals.cancel_cta}
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
        isLoading={isProcessingAction}
        error={confirmModalError}
      />
    </Fragment>
  );
}

SpendSafeguards.propTypes = {
  content: PropTypes.object.isRequired,
  isPaidPlan: PropTypes.bool.isRequired,
  refreshTrigger: PropTypes.number.isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
  onAddRuleClick: PropTypes.func.isRequired,
  onEditRuleClick: PropTypes.func.isRequired,
};

export default SpendSafeguards;
